'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export default function BrowserVoltageDividerCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('voltage-divider-calculator', '').catch(() => undefined);
  };

  const [vin, setVin] = useState(12);
  const [r1, setR1] = useState(1000);
  const [r2, setR2] = useState(1000);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const total = r1 + r2;
    const vout = total > 0 ? (vin * r2) / total : 0;
    const current = total > 0 ? vin / total : 0;
    return { vout, current, total };
  }, [vin, r1, r2]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Electronics</span>
        <h2 className="tool-live-title">Voltage divider calculator</h2>
        <p className="tool-live-subtitle">
          Enter the input voltage and 2 resistor values to get the output voltage across R2.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Input voltage, Vin (V)</span>
            <input
              onChange={(e) => onChange(setVin)(Number(e.target.value) || 0)}
              step="any"
              type="number"
              value={vin}
            />
          </label>
          <label className="tool-live-field">
            <span>R1 (Ω)</span>
            <input
              min={0}
              onChange={(e) => onChange(setR1)(Number(e.target.value) || 0)}
              step="any"
              type="number"
              value={r1}
            />
          </label>
          <label className="tool-live-field">
            <span>R2 (Ω)</span>
            <input
              min={0}
              onChange={(e) => onChange(setR2)(Number(e.target.value) || 0)}
              step="any"
              type="number"
              value={r2}
            />
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.vout.toFixed(4)} V</strong>
            <span>Output voltage, Vout (measured across R2)</span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{(result.current * 1000).toFixed(3)} mA</strong>
            <span>Current through the divider</span>
          </p>
          <p className="tool-live-panel-note">
            Vout = Vin x R2 / (R1 + R2). This is an unloaded (no load resistor) divider; adding a
            load in parallel with R2 pulls Vout down.
          </p>
        </section>
      </div>
    </div>
  );
}
